import React, { useEffect, useState } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  Box, Button, ButtonGroup, Text, Textarea, FormControl, FormLabel, FormErrorMessage,
} from '@chakra-ui/react';
import { FINDING_CATEGORY, FINDING_IMPACT } from 'api/administrador/ventanillaSimulator';

/**
 * Reporte sobre una respuesta del agente. Solo pide lo que el evaluador sabe; la conversación, el
 * mensaje, el modelo, el guion y las herramientas los guarda la plataforma.
 */

function Choice({ options, value, onChange, allowEmpty }) {
  return (
    <ButtonGroup size="sm" isAttached={false} spacing={2} flexWrap="wrap">
      {Object.entries(options).map(([key, opt]) => {
        const active = value === key;
        return (
          <Button
            key={key} mb={1} borderRadius="8px" fontWeight="500"
            variant={active ? 'solid' : 'outline'} bg={active ? '#574feb' : 'white'} color={active ? 'white' : 'gray.700'}
            _hover={{ bg: active ? '#4a42d6' : 'gray.50' }}
            onClick={() => onChange(active && allowEmpty ? '' : key)}
            aria-pressed={active}
          >
            {typeof opt === 'string' ? opt : opt.label}
          </Button>
        );
      })}
    </ButtonGroup>
  );
}

export default function FindingModal({ isOpen, onClose, onSubmit, message }) {
  const [category, setCategory] = useState('respuesta_incorrecta');
  const [description, setDescription] = useState('');
  const [expected, setExpected] = useState('');
  const [impact, setImpact] = useState('');
  const [missing, setMissing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setCategory('respuesta_incorrecta');
    setDescription('');
    setExpected('');
    setImpact('');
    setMissing(false);
  }, [isOpen]);

  const submit = async () => {
    if (!description.trim()) { setMissing(true); return; }
    setSaving(true);
    try {
      await onSubmit({ category, description: description.trim(), expected: expected.trim(), impact: impact || undefined });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent borderRadius="14px">
        <ModalHeader fontSize="17px">Reportar respuesta</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {message?.text && (
            <Box bg="gray.50" borderRadius="8px" px={3} py={2} mb={4} maxH="96px" overflowY="auto">
              <Text fontSize="12px" color="gray.600" whiteSpace="pre-wrap" noOfLines={4}>{message.text}</Text>
            </Box>
          )}
          <FormControl mb={4}>
            <FormLabel fontSize="13px" mb={2}>Tipo</FormLabel>
            <Choice options={FINDING_CATEGORY} value={category} onChange={setCategory} />
          </FormControl>
          <FormControl mb={4} isInvalid={missing} isRequired>
            <FormLabel htmlFor="finding-description" fontSize="13px" mb={1}>¿Qué pasó?</FormLabel>
            <Textarea
              id="finding-description" rows={3} fontSize="14px" borderRadius="8px"
              placeholder="Dijo que la constancia de pensión salía automática"
              value={description} onChange={(e) => { setDescription(e.target.value); setMissing(false); }}
            />
            <FormErrorMessage>Cuenta qué pasó.</FormErrorMessage>
          </FormControl>
          <FormControl mb={4}>
            <FormLabel htmlFor="finding-expected" fontSize="13px" mb={1}>¿Qué esperabas?</FormLabel>
            <Textarea
              id="finding-expected" rows={2} fontSize="14px" borderRadius="8px"
              placeholder="Que registrara la solicitud y la pasara al equipo"
              value={expected} onChange={(e) => setExpected(e.target.value)}
            />
          </FormControl>
          <FormControl>
            <FormLabel fontSize="13px" mb={2}>Impacto</FormLabel>
            <Choice options={FINDING_IMPACT} value={impact} onChange={setImpact} allowEmpty />
          </FormControl>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button bg="#574feb" color="white" _hover={{ bg: '#4a42d6' }} onClick={submit} isLoading={saving}>Enviar reporte</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
